import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { tenantWorkspaceApi } from '@/core/api/tenantWorkspaceApi';
import type { PublicPersonalLanding } from '@/core/types/tenantWorkspace';
import { formatDateTime } from '@/core/utils/date';
import { AppIcon } from '@/shared/components/AppIcon';
import { resolveCatalogResourceUrl } from '@/shared/components/CatalogResource';

interface LandingItemSource {
  catalogItemId?: string | null;
  productId?: string | null;
  id?: string | null;
  name?: string | null;
  productName?: string | null;
  itemType?: 'Product' | 'Service' | null;
  imageUrl?: string | null;
  price?: number | null;
  previousPrice?: number | null;
  currency?: string | null;
}

type LandingWithItems = PublicPersonalLanding & {
  items?: LandingItemSource[] | null;
  productId?: string | null;
};

interface LandingCardItem {
  key: string;
  productId: string | null;
  name: string;
  itemType: 'Product' | 'Service';
  imageUrl: string | null;
  price: number | null;
  previousPrice: number | null;
  currency: string;
}

interface InteractionEvent {
  eventType: 'CTA_Clicked' | 'Reserved';
  productId?: string;
  itemKey: string;
}

const money = (value: number | null, currency: string): string => (
  value === null
    ? 'برای استعلام تماس بگیرید'
    : `${new Intl.NumberFormat('fa-IR').format(value)} ${{ IRR: 'ریال', IRT: 'تومان', USD: 'دلار', EUR: 'یورو' }[currency] ?? currency}`
);

const normalizeItemSources = (sources: LandingItemSource[], fallbackCurrency: string): LandingCardItem[] => {
  const seen = new Set<string>();

  return sources.reduce<LandingCardItem[]>((items, source, index) => {
    const productId = source.catalogItemId ?? source.productId ?? source.id ?? null;
    const name = source.name?.trim() || source.productName?.trim() || `مورد ${new Intl.NumberFormat('fa-IR').format(index + 1)}`;
    const distinctKey = productId
      ? `product:${productId}`
      : `legacy:${name}|${source.itemType ?? 'Product'}|${source.imageUrl ?? ''}|${source.price ?? ''}`;

    if (seen.has(distinctKey)) return items;
    seen.add(distinctKey);
    items.push({
      key: distinctKey,
      productId,
      name,
      itemType: source.itemType === 'Service' ? 'Service' : 'Product',
      imageUrl: source.imageUrl ?? null,
      price: source.price ?? null,
      previousPrice: source.previousPrice ?? null,
      currency: source.currency || fallbackCurrency || 'IRR',
    });
    return items;
  }, []);
};

const normalizeItems = (data: LandingWithItems): LandingCardItem[] => {
  const sources: LandingItemSource[] = data.items?.length
    ? data.items
    : [{
        productId: data.productId,
        productName: data.productName,
        itemType: 'Product',
        imageUrl: data.imageUrl,
        price: data.price,
        previousPrice: data.previousPrice,
        currency: data.currency,
      }];
  return normalizeItemSources(sources, data.currency);
};

export const PublicPersonalLandingPage = (): React.ReactElement => {
  const token = useParams().token ?? '';
  const viewedItems = useRef<Set<string>>(new Set());
  const itemGrid = useRef<HTMLElement>(null);
  const [reservedItemKeys, setReservedItemKeys] = useState<Set<string>>(() => new Set());
  const landing = useQuery({
    queryKey: ['public-landing', token],
    queryFn: () => tenantWorkspaceApi.publicPersonalLanding(token),
    enabled: Boolean(token),
  });
  const interaction = useMutation({
    mutationFn: ({ eventType, productId }: InteractionEvent) => tenantWorkspaceApi.trackPersonalLanding(token, eventType, productId),
    onSuccess: (_response, variables) => {
      if (variables.eventType !== 'Reserved') return;
      setReservedItemKeys((current) => new Set(current).add(variables.itemKey));
    },
  });

  useEffect(() => {
    const grid = itemGrid.current;
    if (!landing.data || !token || !grid) return;

    const itemsByKey = new Map(normalizeItems(landing.data as LandingWithItems).map(item => [item.key, item]));
    const cards = Array.from(grid.querySelectorAll<HTMLElement>('[data-landing-item-key]'));
    let observer: IntersectionObserver | null = null;
    const recordView = (card: HTMLElement): void => {
      const item = itemsByKey.get(card.dataset.landingItemKey ?? '');
      if (!item) return;
      const eventKey = `${token}:${item.key}`;
      if (viewedItems.current.has(eventKey)) return;

      viewedItems.current.add(eventKey);
      void tenantWorkspaceApi
        .trackPersonalLanding(token, 'ProductViewed', item.productId ?? undefined)
        .then(() => observer?.unobserve(card))
        .catch(() => viewedItems.current.delete(eventKey));
    };

    if (!('IntersectionObserver' in window)) {
      cards.slice(0, 1).forEach(recordView);
      return;
    }

    observer = new IntersectionObserver((entries) => {
      entries.filter(entry => entry.isIntersecting).forEach(entry => recordView(entry.target as HTMLElement));
    }, { threshold: 0.35 });
    cards.forEach(card => observer?.observe(card));
    return () => observer?.disconnect();
  }, [landing.data, token]);

  if (landing.isLoading) {
    return (
      <main className="grid min-h-screen place-items-center bg-slate-50 px-6 font-sans text-sm font-bold text-slate-400" dir="rtl">
        <div className="text-center">
          <AppIcon className="mx-auto animate-pulse text-brand-500" name="sparkles" size={36} />
          <p className="mt-4">در حال آماده‌سازی پیشنهاد شما…</p>
        </div>
      </main>
    );
  }

  if (landing.isError || !landing.data) {
    return (
      <main className="grid min-h-screen place-items-center bg-slate-50 p-6 text-center font-sans" dir="rtl">
        <div>
          <AppIcon className="mx-auto text-rose-400" name="close" size={36} />
          <h1 className="mt-4 text-xl font-black text-slate-800">این صفحه در دسترس نیست</h1>
          <p className="mt-2 text-sm text-slate-500">ممکن است لینک کامل نباشد یا دیگر اعتبار نداشته باشد.</p>
        </div>
      </main>
    );
  }

  const data = landing.data as LandingWithItems;
  const items = normalizeItems(data);
  const previousIntroductions = (data.previousIntroductions ?? [])
    .map(introduction => ({
      ...introduction,
      items: normalizeItemSources(introduction.items ?? [], data.currency),
    }))
    .filter(introduction => introduction.items.length > 0);
  const call = async (item: LandingCardItem): Promise<void> => {
    try {
      await interaction.mutateAsync({ eventType: 'CTA_Clicked', productId: item.productId ?? undefined, itemKey: item.key });
    } finally {
      if (data.storePhone) window.location.href = `tel:${data.storePhone}`;
    }
  };
  const reserve = (item: LandingCardItem): void => {
    interaction.mutate({ eventType: 'Reserved', productId: item.productId ?? undefined, itemKey: item.key });
  };

  return (
    <main className="min-h-screen bg-slate-50 font-sans text-right text-slate-900" dir="rtl">
      <header className="relative overflow-hidden bg-gradient-to-bl from-brand-700 via-brand-600 to-violet-700 px-4 py-10 text-white sm:py-14">
        {data.storeBannerUrl ? <img alt="" className="absolute inset-0 size-full object-cover opacity-35" onError={({ currentTarget }) => { currentTarget.hidden = true; }} src={resolveCatalogResourceUrl(data.storeBannerUrl)} /> : null}
        {data.storeBannerUrl ? <div className="absolute inset-0 bg-gradient-to-bl from-brand-800/85 via-brand-700/75 to-violet-800/85" /> : null}
        <div className="absolute -left-16 -top-20 size-64 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -bottom-24 right-1/4 size-72 rounded-full bg-violet-300/20 blur-3xl" />
        <div className="relative mx-auto max-w-6xl">
          <p className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1.5 text-xs font-bold ring-1 ring-white/20 backdrop-blur-sm">
            <AppIcon name="building" size={16} />
            {data.storeName}
          </p>
          <h1 className="mt-5 max-w-3xl text-2xl font-black leading-relaxed sm:text-4xl">
            {data.customerName ? `${data.customerName} عزیز، ` : ''}این پیشنهادها برای شماست
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-7 text-white/75 sm:text-base">
            {items.length > 1
              ? `${new Intl.NumberFormat('fa-IR').format(items.length)} کالا و خدمت منتخب را بررسی کنید و مورد مناسب را رزرو کنید.`
              : 'جزئیات پیشنهاد را ببینید و در صورت تمایل، همین حالا رزرو کنید.'}
          </p>
          {data.expiresAtUtc ? (
            <p className="mt-5 inline-flex items-center gap-2 rounded-xl bg-slate-950/20 px-3 py-2 text-xs font-bold text-white/90">
              <AppIcon name="calendar" size={16} />
              اعتبار پیشنهاد تا {formatDateTime(data.expiresAtUtc)}
            </p>
          ) : null}
        </div>
      </header>

      <section className="mx-auto grid max-w-6xl gap-5 px-4 py-8 sm:grid-cols-2 sm:py-10 lg:grid-cols-3" ref={itemGrid}>
        {items.map((item) => {
          const itemLabel = item.itemType === 'Service' ? 'خدمت' : 'کالا';
          const isCurrentInteraction = interaction.variables?.itemKey === item.key;
          const isReserving = isCurrentInteraction && interaction.variables?.eventType === 'Reserved' && interaction.isPending;
          const reservationSucceeded = isCurrentInteraction && interaction.variables?.eventType === 'Reserved' && interaction.isSuccess;
          const reservationFailed = isCurrentInteraction && interaction.variables?.eventType === 'Reserved' && interaction.isError;
          const reservationRecorded = reservedItemKeys.has(item.key);

          return (
            <article className="flex min-w-0 flex-col overflow-hidden rounded-3xl bg-white shadow-lg shadow-slate-200/70 ring-1 ring-slate-200/70" data-landing-item-key={item.key} key={item.key}>
              <div className="relative grid aspect-[4/3] place-items-center overflow-hidden bg-gradient-to-bl from-brand-100 to-violet-100">
                <AppIcon className="text-brand-400" name={item.itemType === 'Service' ? 'sparkles' : 'box'} size={64} />
                {item.imageUrl ? (
                  <img
                    alt={item.name}
                    className="absolute inset-0 size-full object-cover transition duration-500 hover:scale-105"
                    loading="lazy"
                    onError={({ currentTarget }) => { currentTarget.hidden = true; }}
                    src={item.imageUrl}
                  />
                ) : null}
                <span className="absolute right-4 top-4 rounded-full bg-white/90 px-3 py-1 text-xs font-black text-brand-700 shadow-sm backdrop-blur-sm">
                  {itemLabel}
                </span>
              </div>

              <div className="flex flex-1 flex-col p-5 sm:p-6">
                <h2 className="text-xl font-black leading-8 text-ink-950">{item.name}</h2>
                <div className="mt-4 min-h-14">
                  {item.previousPrice !== null ? (
                    <p className="text-xs font-bold text-slate-400 line-through">{money(item.previousPrice, item.currency)}</p>
                  ) : null}
                  <p className="mt-1 text-xl font-black text-brand-700">{money(item.price, item.currency)}</p>
                </div>

                <div className="mt-auto grid gap-2.5 pt-6 sm:grid-cols-2">
                  <button
                    className="rounded-xl bg-brand-600 px-4 py-3 text-sm font-black text-white transition hover:bg-brand-700 disabled:cursor-wait disabled:opacity-60"
                    disabled={isReserving || reservationRecorded}
                    onClick={() => reserve(item)}
                    type="button"
                  >
                    {isReserving ? 'در حال ثبت…' : reservationRecorded ? 'درخواست ثبت شد' : `درخواست رزرو ${itemLabel}`}
                  </button>
                  <button
                    className="rounded-xl border border-slate-200 px-4 py-3 text-sm font-black text-slate-700 transition hover:border-brand-200 hover:bg-brand-50 hover:text-brand-700 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400"
                    disabled={!data.storePhone}
                    onClick={() => void call(item)}
                    type="button"
                  >
                    {data.storePhone ? 'تماس با فروشگاه' : 'تماس ناموجود'}
                  </button>
                </div>

                {reservationSucceeded ? (
                  <p className="mt-3 rounded-xl bg-emerald-50 p-3 text-center text-xs font-bold text-emerald-700">
                    درخواست رزرو این {itemLabel} ثبت شد؛ فروشگاه پس از بررسی موجودی با شما هماهنگ می‌کند.
                  </p>
                ) : null}
                {reservationFailed ? (
                  <p className="mt-3 rounded-xl bg-rose-50 p-3 text-center text-xs font-bold text-rose-700">
                    ثبت درخواست انجام نشد؛ لطفاً دوباره تلاش کنید.
                  </p>
                ) : null}
              </div>
            </article>
          );
        })}
      </section>

      {previousIntroductions.length ? (
        <section className="border-y border-slate-200 bg-white/70 px-4 py-9 sm:py-12">
          <div className="mx-auto max-w-6xl">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="text-xs font-black text-brand-600">سابقه پیشنهادهای شما</p>
                <h2 className="mt-1 text-xl font-black text-ink-950 sm:text-2xl">معرفی‌های قبلی شما</h2>
                <p className="mt-2 max-w-2xl text-sm leading-7 text-slate-500">
                  برای یادآوری، سه معرفی قبلی شما در این فروشگاه نمایش داده شده است. قیمت‌ها مربوط به زمان همان معرفی هستند.
                </p>
              </div>
              <span className="w-fit rounded-full bg-brand-50 px-3 py-1.5 text-xs font-black text-brand-700">
                {new Intl.NumberFormat('fa-IR').format(previousIntroductions.length)} مراجعه قبلی
              </span>
            </div>

            <div className="mt-6 space-y-5">
              {previousIntroductions.map((introduction) => (
                <article className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5" key={introduction.landingId}>
                  <header className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-3">
                    <div className="flex items-center gap-2 text-sm font-black text-slate-700">
                      <span className="grid size-9 place-items-center rounded-xl bg-slate-100 text-brand-600"><AppIcon name="calendar" size={17} /></span>
                      معرفی در {formatDateTime(introduction.introducedAtUtc)}
                    </div>
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-500">
                      {new Intl.NumberFormat('fa-IR').format(introduction.items.length)} مورد
                    </span>
                  </header>

                  <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {introduction.items.map(item => (
                      <div className="flex min-w-0 items-center gap-3 rounded-2xl bg-slate-50 p-3" key={`${introduction.landingId}:${item.key}`}>
                        <div className="relative grid size-16 shrink-0 place-items-center overflow-hidden rounded-xl bg-gradient-to-bl from-brand-100 to-violet-100 text-brand-500">
                          <AppIcon name={item.itemType === 'Service' ? 'sparkles' : 'box'} size={25} />
                          {item.imageUrl ? <img alt={item.name} className="absolute inset-0 size-full object-cover" loading="lazy" onError={({ currentTarget }) => { currentTarget.hidden = true; }} src={item.imageUrl} /> : null}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex min-w-0 items-center gap-2">
                            <p className="truncate text-sm font-black text-slate-800">{item.name}</p>
                            <span className="shrink-0 rounded bg-white px-1.5 py-0.5 text-[10px] font-bold text-slate-500">{item.itemType === 'Service' ? 'خدمت' : 'کالا'}</span>
                          </div>
                          <p className="mt-2 text-xs font-black text-brand-700">{money(item.price, item.currency)}</p>
                          <p className="mt-1 text-[10px] font-bold text-slate-400">قیمت در زمان معرفی</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <footer className="px-4 pb-8 text-center text-xs leading-6 text-slate-400">
        با رزرو هر مورد، درخواست شما برای پیگیری در فروشگاه ثبت می‌شود.
      </footer>
    </main>
  );
};
